import * as XLSX from "xlsx";
import { XMLParser } from "fast-xml-parser";
import proj4 from "proj4";
import { isAgriHeaderRow, parseAgriMatrix } from "./agriFormat";
import { cropLabel } from "./cropCodes";

// Lambert-93 (EPSG:2154) — projection officielle des exports Télépac / cadastre FR
proj4.defs(
  "EPSG:2154",
  "+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
);

let _id = 0;
const nextId = () => `p_${Date.now()}_${_id++}`;

// ---------- Helpers géométrie ----------

// Reprojette une paire [x, y] vers [lat, lng] WGS84.
// Détecte automatiquement Lambert-93 (grandes valeurs) vs WGS84.
function pairToLatLng(a, b) {
  // a,b proviennent d'un flux gml posList: ordre habituel "X Y" (lon/est, lat/nord)
  if (Math.abs(a) > 180 || Math.abs(b) > 180) {
    // Lambert-93 (mètres) -> reprojection
    const [lng, lat] = proj4("EPSG:2154", "EPSG:4326", [a, b]);
    return [lat, lng];
  }
  // Déjà en degrés. En GML 4326, l'ordre standard est lat puis lon.
  return [a, b];
}

// Convertit une liste plate de nombres "x y x y ..." en anneau [[lat,lng],...]
function flatListToRing(numbers) {
  const ring = [];
  for (let i = 0; i + 1 < numbers.length; i += 2) {
    ring.push(pairToLatLng(numbers[i], numbers[i + 1]));
  }
  return ring;
}

function parseNumbersFromString(str) {
  if (!str) return [];
  return str
    .toString()
    .trim()
    .split(/[\s,]+/)
    .map((n) => parseFloat(n))
    .filter((n) => !isNaN(n));
}

// ---------- Parsing Excel (.xlsx) ----------

const HEADER_MAP = {
  nom: "name",
  "nom parcelle": "name",
  "nom de la parcelle": "name",
  parcelle: "name",
  "parcel name": "name",
  proprietaire: "owner",
  propriétaire: "owner",
  owner: "owner",
  "owner name": "owner",
  surface: "area",
  "surface (ha)": "area",
  "surface ha": "area",
  contenance: "area",
  area: "area",
  "area (ha)": "area",
  notes: "notes",
  note: "notes",
  remarques: "notes",
  culture: "crop",
  crop: "crop",
  "type de culture": "crop",
  commune: "commune",
  "code insee": "code_insee",
  insee: "code_insee",
  section: "section",
  numero: "numero",
  numéro: "numero",
  "numero parcelle": "numero",
  coordonnees: "coordinates",
  coordonnées: "coordinates",
  coordinates: "coordinates",
  geometrie: "coordinates",
};

function normalizeKey(k) {
  const key = k.toString().trim().toLowerCase();
  return HEADER_MAP[key] || key;
}

// Coordonnées Excel: "lat,lng; lat,lng; ..." (degrés WGS84)
function parseCoordinateCell(cell) {
  if (!cell) return null;
  const pairs = cell
    .toString()
    .split(/[;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const ring = [];
  for (const p of pairs) {
    const nums = parseNumbersFromString(p);
    if (nums.length >= 2) ring.push([nums[0], nums[1]]);
  }
  return ring.length >= 3 ? ring : null;
}

export function parseExcel(arrayBuffer) {
  const wb = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

  // Détection du format Agri-cadastre (en-têtes sur une ligne non systématiquement la 1ère)
  for (let i = 0; i < Math.min(matrix.length, 12); i++) {
    if (isAgriHeaderRow(matrix[i])) {
      return parseAgriMatrix(matrix, i).map((p) => ({ id: nextId(), ...p }));
    }
  }

  // Format simple (modèle fourni) : en-têtes sur la 1ère ligne, coordonnées WGS84
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
  const parcels = [];
  for (const raw of rows) {
    const row = {};
    for (const [k, v] of Object.entries(raw)) {
      row[normalizeKey(k)] = v;
    }
    if (!row.name && !row.numero && !row.coordinates) continue;

    const polygon = parseCoordinateCell(row.coordinates);
    parcels.push({
      id: nextId(),
      name: (row.name || row.numero || "Parcelle").toString(),
      owner: (row.owner || "").toString(),
      area: row.area !== "" ? parseFloat(row.area) || null : null,
      notes: (row.notes || "").toString(),
      crop: (row.crop || "").toString(),
      commune: (row.commune || "").toString(),
      code_insee: (row.code_insee || "").toString().trim(),
      section: (row.section || "").toString().trim(),
      numero: (row.numero || "").toString().trim(),
      source: "excel",
      polygon: polygon,
      needsGeometry: !polygon && !!(row.code_insee || row.section),
    });
  }
  return parcels;
}

// ---------- Parsing XML (Télépac / PAC, GML Lambert-93) ----------

// Récupère récursivement la première liste de coordonnées trouvée dans un noeud.
function extractRingsFromNode(node) {
  const rings = [];

  function visit(n) {
    if (n == null || typeof n !== "object") return;
    for (const [key, value] of Object.entries(n)) {
      const lk = key.toLowerCase();
      if (lk.includes("poslist") || lk.includes("coordinates") || lk === "coordonnees") {
        const text = typeof value === "object" ? value["#text"] || "" : value;
        const ring = flatListToRing(parseNumbersFromString(text));
        if (ring.length >= 3) rings.push(ring);
      } else if (Array.isArray(value)) {
        value.forEach(visit);
      } else if (typeof value === "object") {
        visit(value);
      }
    }
  }

  visit(node);
  return rings;
}

// Récupère les attributs scalaires "intéressants" d'un noeud (peu profond).
function extractAttributes(node) {
  const attrs = {};
  function visit(n, depth) {
    if (n == null || typeof n !== "object" || depth > 3) return;
    for (const [key, value] of Object.entries(n)) {
      const lk = key.toLowerCase().replace(/^@_?/, "");
      if (
        typeof value === "string" ||
        typeof value === "number"
      ) {
        if (lk.includes("poslist") || lk.includes("coord") || lk === "#text") continue;
        if (attrs[lk] === undefined) attrs[lk] = value;
      } else if (typeof value === "object" && !Array.isArray(value)) {
        visit(value, depth + 1);
      }
    }
  }
  visit(node, 0);
  return attrs;
}

function attr(attrs, ...keys) {
  for (const k of keys) {
    for (const ak of Object.keys(attrs)) {
      if (ak.includes(k)) return attrs[ak];
    }
  }
  return "";
}

export function parseXML(text) {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    removeNSPrefix: true,
    parseTagValue: true,
    trimValues: true,
  });
  const doc = parser.parse(text);

  // Détection de la campagne Télépac (attribut "campagne" quelque part dans le document).
  let campaign = null;
  (function scanCampaign(n, depth) {
    if (campaign || n == null || typeof n !== "object" || depth > 8) return;
    for (const [key, value] of Object.entries(n)) {
      const lk = key.toLowerCase().replace(/^@_?/, "");
      if ((typeof value === "string" || typeof value === "number") && lk.includes("campagne")) {
        const y = parseInt(value, 10);
        if (y >= 2000 && y <= 2100) {
          campaign = y;
          return;
        }
      } else if (value && typeof value === "object") {
        const items = Array.isArray(value) ? value : [value];
        for (const it of items) scanCampaign(it, depth + 1);
      }
    }
  })(doc, 0);

  const parcels = [];

  // Recherche des nœuds "parcelle" (puis repli sur "ilot" si aucune parcelle).
  function findNodesByKey(node, targetKey, results) {
    if (node == null || typeof node !== "object") return;
    for (const [key, value] of Object.entries(node)) {
      const lk = key.toLowerCase();
      const items = Array.isArray(value) ? value : [value];
      if (lk === targetKey) {
        for (const it of items) if (it && typeof it === "object") results.push(it);
      }
      for (const it of items) {
        if (it && typeof it === "object") findNodesByKey(it, targetKey, results);
      }
    }
  }

  let nodes = [];
  findNodesByKey(doc, "parcelle", nodes);
  if (nodes.length === 0) findNodesByKey(doc, "ilot", nodes);

  const usedGeom = new Set();
  for (const node of nodes) {
    const rings = extractRingsFromNode(node);
    if (rings.length === 0) continue;
    const attrs = extractAttributes(node);

    const numero =
      attr(attrs, "numeroparcelle", "numero", "numparcelle", "numeroilot") || "";
    const culture =
      attr(attrs, "libelleculture", "culture", "codeculture") || "";
    const surface = parseFloat(
      attr(attrs, "surfaceadmissible", "surfacemesuree", "surface", "contenance")
    );

    rings.forEach((ring, idx) => {
      const key = JSON.stringify(ring[0]) + "|" + numero;
      if (usedGeom.has(key)) return;
      usedGeom.add(key);
      parcels.push({
        id: nextId(),
        name:
          (numero ? `Parcelle ${numero}` : "Parcelle") +
          (rings.length > 1 ? ` (${idx + 1})` : ""),
        owner: attr(attrs, "proprietaire", "exploitant", "raisonsociale") || "",
        area: isNaN(surface) ? null : surface,
        notes: "",
        crop: culture,
        crop_label: cropLabel(culture),
        commune: (attr(attrs, "commune", "codecommune") || "").toString(),
        code_insee: (attr(attrs, "commune", "codecommune") || "").toString(),
        section: attr(attrs, "section") || "",
        numero: numero.toString(),
        source: "xml",
        campaign,
        polygon: ring,
        needsGeometry: false,
      });
    });
  }

  return parcels;
}

// ---------- Point d'entrée ----------

export async function parseFile(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    const buf = await file.arrayBuffer();
    return parseExcel(buf);
  }
  if (name.endsWith(".xml")) {
    const text = await file.text();
    return parseXML(text);
  }
  throw new Error("Format non supporté. Utilisez un fichier .xlsx ou .xml");
}
